import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { ApiHelper } from "api/ApiHelper";
import { AppContext } from "App";

/**
 * MYA (market year average) prices by commodity for projected indemnity price inputs.
 */
export function useMarketYearAveragePrices({ commodities, crop_year, typeNameByCommodity = {}, apiConfig = null }) {
  const { globalState: contextGlobalState } = useContext(AppContext);
  const globalState = apiConfig || contextGlobalState;

  const [pricesByCommodity, setPricesByCommodity] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const commoditiesKey = useMemo(
    () => [...new Set((commodities || []).filter(Boolean))].sort().join("|"),
    [commodities]
  );

  const typeNameKey = useMemo(() => JSON.stringify(typeNameByCommodity || {}), [typeNameByCommodity]);

  const loadPrices = useCallback(async () => {
    const uniqueCommodities = commoditiesKey ? commoditiesKey.split("|") : [];
    if (!crop_year || !uniqueCommodities.length) {
      setPricesByCommodity({});
      return;
    }

    const typeMap = typeNameKey ? JSON.parse(typeNameKey) : {};

    try {
      setIsLoading(true);
      const next = {};

      for (const commodity_name of uniqueCommodities) {
        const filter = {
          crop_year,
          commodity_name,
        };
        const type_name = typeMap[commodity_name];
        if (type_name) {
          filter.type_name = type_name;
        }

        const rows = await ApiHelper.selectObject("FSA.ARCPLC", "MarketYearAveragePrices", filter);
        if (rows?.length) {
          next[commodity_name] = rows[0];
        }
      }

      setPricesByCommodity(next);
    } catch (err) {
      console.warn("Error loading MarketYearAveragePrices:", err);
      setPricesByCommodity({});
    } finally {
      setIsLoading(false);
    }
  }, [crop_year, commoditiesKey, typeNameKey]);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  return { pricesByCommodity, isLoading };
}
